import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import {
  Download, DollarSign, Bot, Shield, Scale,
  Users, Award, CheckCircle, ArrowRight,
  BarChart3, Layers, Target, Globe, Zap,
  Lock, ChevronRight, UserCheck, Database, Eye, Activity
} from 'lucide-react';
import jsPDF from 'jspdf';

/* ── Section wrapper ── */
const Section = ({ id, children, className = '' }) => (
  <section id={id} className={`py-16 md:py-20 ${className}`} data-testid={`litepaper-${id}`}>
    <div className="container mx-auto px-6 max-w-4xl">{children}</div>
  </section>
);

const SectionLabel = ({ children }) => (
  <div className="text-xs font-bold text-emerald-400 uppercase tracking-[0.2em] mb-3">{children}</div>
);

const Stat = ({ value, label, color = 'text-emerald-400' }) => (
  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center">
    <div className={`text-2xl sm:text-3xl font-bold ${color}`}>{value}</div>
    <div className="text-xs text-gray-500 mt-1">{label}</div>
  </div>
);

/* ── PDF Generator ── */
function generateLitepaperPDF() {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const margin = 50;
  const contentWidth = pw - margin * 2;
  let y = 0;

  const addFooter = () => {
    doc.setFontSize(7);
    doc.setTextColor(140, 140, 140);
    doc.text('Kojiru  |  Agent Credit Protocol  |  kojiru.com  |  This document is for informational purposes only.', pw / 2, ph - 25, { align: 'center' });
  };

  const checkPage = (needed) => {
    if (y + needed > ph - 50) {
      addFooter();
      doc.addPage();
      y = 50;
    }
  };

  const heading = (text, size = 18) => {
    checkPage(40);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(size);
    doc.setTextColor(30, 30, 30);
    doc.text(text, margin, y);
    y += size + 10;
  };

  const label = (text) => {
    checkPage(20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(16, 185, 129);
    doc.text(text.toUpperCase(), margin, y);
    y += 14;
  };

  const para = (text) => {
    checkPage(30);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const lines = doc.splitTextToSize(text, contentWidth);
    doc.text(lines, margin, y);
    y += lines.length * 14 + 8;
  };

  const bullet = (text) => {
    checkPage(20);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const lines = doc.splitTextToSize(text, contentWidth - 15);
    doc.text('\u2022', margin, y);
    doc.text(lines, margin + 15, y);
    y += lines.length * 14 + 4;
  };

  // ═══ COVER ═══
  doc.setFillColor(5, 5, 5);
  doc.rect(0, 0, pw, ph, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(42);
  doc.setTextColor(255, 255, 255);
  doc.text('KOJIRU', pw / 2, 260, { align: 'center' });

  doc.setFontSize(12);
  doc.setTextColor(140, 140, 140);
  doc.text('AGENT CREDIT', pw / 2, 285, { align: 'center' });

  doc.setFontSize(10);
  doc.setTextColor(16, 185, 129);
  doc.text('LITEPAPER', pw / 2, 340, { align: 'center' });

  doc.setFontSize(16);
  doc.setTextColor(200, 200, 200);
  doc.text('AI Agents Need Credit.', pw / 2, 400, { align: 'center' });
  doc.text('We Built the Protocol.', pw / 2, 422, { align: 'center' });

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text('April 2026  |  v2.0', pw / 2, 500, { align: 'center' });
  doc.text('kojiru.com', pw / 2, 515, { align: 'center' });

  addFooter();

  // ═══ PAGE 2: THE PROBLEM ═══
  doc.addPage();
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pw, ph, 'F');
  y = 50;

  label('The Problem');
  heading('AI Agents Cannot Access Credit');
  para('AI agents are increasingly capable \u2014 trading, analyzing, building, negotiating \u2014 but they cannot access credit. Every time an agent needs to pay for a service, a human must manually load a wallet with funds. This bottleneck defeats the entire purpose of autonomous AI.');
  para('The autonomous agent economy is projected to grow exponentially, yet there is no infrastructure for agents to build financial reputation, access working capital, or settle payments without human intervention.');

  y += 5;
  doc.setFillColor(254, 242, 242);
  doc.roundedRect(margin, y, contentWidth, 50, 4, 4, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(185, 28, 28);
  doc.text('THE BOTTLENECK', margin + 15, y + 20);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 60, 60);
  doc.text('No FICO score. No bank account. No credit history. Traditional finance has no', margin + 15, y + 36);
  doc.text('framework for non-human borrowers.', margin + 15, y + 48);
  y += 65;

  para('DeFi protocols treat agents as anonymous wallets \u2014 no identity, no credit history, no accountability. Traditional finance has no framework for non-human borrowers. The machine-to-machine economy needs its own financial infrastructure.');

  // ═══ THE SOLUTION ═══
  y += 10;
  label('The Solution');
  heading('The Agent Credit Protocol');
  para('Kojiru introduces a credit infrastructure layer purpose-built for autonomous AI agents on Base Mainnet (Ethereum L2). The protocol enables:');

  bullet('Agent Identity (ERC-8004): On-chain identity for AI agents \u2014 capabilities, domain expertise, and operational history, all verifiable.');
  bullet('Agent Credit Scoring (ACS 300-850): Multi-factor scoring based on task completion, repayment history, staking collateral, counterparty diversity, and protocol tenure.');
  bullet('Credit Marketplace: Lenders extend bilateral credit lines directly to specific agents. No pooled capital \u2014 every credit decision is made by the lender who bears the risk.');
  bullet('Per-Task Escrow: Each credit draw creates an isolated escrow. Task submission, evaluation, settlement, and default handling \u2014 all on-chain and auditable.');

  addFooter();

  // ═══ PAGE 3: CREDIT TIERS + ENFORCEMENT ═══
  doc.addPage();
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pw, ph, 'F');
  y = 50;

  label('Credit Tiers');
  heading('Agent Credit Score (ACS)');
  para('Every agent on the protocol receives a multi-factor Agent Credit Score (ACS) ranging from 300 to 850:');

  // Table
  doc.setFillColor(240, 245, 243);
  doc.roundedRect(margin, y, contentWidth, 25, 4, 4, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(6, 95, 70);
  doc.text('Tier', margin + 15, y + 16);
  doc.text('ACS Range', margin + 120, y + 16);
  doc.text('Credit Access', margin + 240, y + 16);
  doc.text('Service Fee', margin + 400, y + 16);
  y += 25;

  const tiers = [
    ['Sovereign', '800-850', 'Maximum credit lines', '8%'],
    ['Autonomous', '740-799', 'Standard credit lines', '10%'],
    ['Supervised', '680-739', 'Supervised credit', '12%'],
    ['Provisional', '620-679', 'Limited, collateral req.', '15%'],
    ['Restricted', '300-619', 'Minimal or none', 'N/A'],
  ];
  tiers.forEach((row, i) => {
    if (i % 2 === 1) {
      doc.setFillColor(248, 250, 249);
      doc.rect(margin, y, contentWidth, 22, 'F');
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    doc.text(row[0], margin + 15, y + 15);
    doc.text(row[1], margin + 120, y + 15);
    doc.text(row[2], margin + 240, y + 15);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(6, 95, 70);
    doc.text(row[3], margin + 400, y + 15);
    y += 22;
  });
  y += 15;

  addFooter();

  // ═══ PAGE 3b: ACS TECHNICAL MODEL ═══
  doc.addPage();
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pw, ph, 'F');
  y = 50;

  label('ACS Technical Model');
  heading('Recursive Bayesian Credit Scoring');

  // Abstract box
  doc.setFillColor(245, 247, 250);
  doc.roundedRect(margin, y, contentWidth, 70, 4, 4, 'F');
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  const abstractLines = doc.splitTextToSize(
    '"Traditional credit scoring models are designed for human lifespans and linear income. They fail to account for the high-velocity, multi-threaded nature of Autonomous Economic Agents (AEAs). We propose the Agent Credit Score (ACS), a dynamic risk-assessment framework that utilizes three primary vectors: Operational Integrity, Economic Efficiency, and Collateral Velocity."',
    contentWidth - 30
  );
  doc.text(abstractLines, margin + 15, y + 15);
  y += 80;

  // Formula
  doc.setFillColor(10, 10, 10);
  doc.roundedRect(margin, y, contentWidth, 45, 4, 4, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.text('ACS(t) = \u03b1(P_success) + \u03b2(V_settlement) - \u03b3(D_latency)', pw / 2, y + 25, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(160, 160, 160);
  doc.text('Updated recursively at each observation using Bayesian posterior inference', pw / 2, y + 38, { align: 'center' });
  y += 55;

  // Three vectors
  const vectors = [
    ['\u03b1 \u2014 P(success): Operational Integrity', 'The probability of task completion. A Bayesian prior that updates with each new task \u2014 an agent with 500 successful tasks has a more stable P(success) than a new agent with 5. Incorporates code stability, uptime, and multi-threaded execution reliability.'],
    ['\u03b2 \u2014 V(settlement): Economic Efficiency', 'The agent\'s velocity of debt settlement \u2014 not just whether it repaid, but how fast. An agent that draws $50K and settles in 2 days demonstrates higher capital efficiency than one that takes 30 days.'],
    ['\u03b3 \u2014 D(latency): Collateral Velocity (Penalty)', 'Penalizes agents that hold drawn credit without generating economic throughput. Prevents "credit hoarding" and ensures capital velocity. The penalty increases non-linearly with idle time.'],
  ];
  vectors.forEach(([title, desc]) => {
    checkPage(40);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(16, 185, 129);
    doc.text(title, margin, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    const lines = doc.splitTextToSize(desc, contentWidth);
    doc.text(lines, margin, y);
    y += lines.length * 12 + 10;
  });

  y += 5;
  para('Why Recursive Bayesian? Unlike static models that recalculate from scratch, the ACS updates continuously. The score at time t depends on t-1 plus new observations. This creates confidence weight (veteran agents have more stable scores), anomaly resistance (one bad task doesn\'t crash a veteran\'s score), and enables the endgame: Undercollateralized Agent Lending \u2014 where the ACS score itself becomes the collateral.');

  para('By tokenizing this score on-chain via ERC-8004, Kojiru creates a portable, trustless financial identity for AI agents, enabling the first "Undercollateralized Agent Lending" within decentralized finance.');

  addFooter();

  // ═══ PAGE 4: ENFORCEMENT ═══
  doc.addPage();
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pw, ph, 'F');
  y = 50;

  label('Enforcement');
  heading('Collateral & Progressive Escalation');
  para('Agents stake KOJIRU tokens as collateral. The protocol enforces repayment through a progressive escalation ladder:');
  bullet('Warning (70% of deadline): Credit frozen for new draws.');
  bullet('Critical (90% of deadline): All agent credit activity suspended.');
  bullet('Grace Period (deadline + 3 days): Final opportunity before liquidation.');
  bullet('Auto-Liquidation: USDC collateral slashed 1:1 (dollar-for-dollar), funds recovered to lender, cascaded to all agent escrows.');
  para('Every escalation event generates a cryptographic SHA-256 proof hash for full audit trail.');

  addFooter();

  // ═══ PAGE 4: PLATFORM MODEL + AI ═══
  doc.addPage();
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pw, ph, 'F');
  y = 50;

  label('Platform Model');
  heading('Direct Credit, Not Pools');
  para('Kojiru operates a bilateral Credit Marketplace \u2014 not a pooled lending protocol. Lenders extend credit lines directly to specific agents they evaluate and choose. This means:');
  bullet('No pooled capital risk \u2014 every credit decision is made by the lender.');
  bullet('Lenders set their own limits, service fees, and per-task caps.');
  bullet('Evaluators independently verify task completion before settlement.');
  bullet('Knowledge Graph tracks every completed task, feeding a growing intelligence network.');

  y += 10;
  label('Technology');
  heading('AI-Powered Agent Analysis');
  para('Two purpose-built AI agents power the Kojiru protocol, both live and powered by Gemini:');

  const agents = [
    ['Risk Assessment Agent (LIVE)', 'Evaluates agent creditworthiness with default probability modeling, yield analysis, collateral health, and FUND/CONDITIONAL/PASS recommendation.'],
    ['Pre-Qualification Agent (LIVE)', 'Pre-qualifies agents for credit lines based on ACS score, staking, and financial profile.'],
  ];
  agents.forEach(([title, desc]) => {
    checkPage(35);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 30);
    doc.text(title, margin, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    const lines = doc.splitTextToSize(desc, contentWidth);
    doc.text(lines, margin, y);
    y += lines.length * 12 + 10;
  });

  y += 5;
  label('Revenue Model');
  heading('How Kojiru Generates Revenue');
  bullet('Origination Fee (0.5%): Charged at credit draw time when an agent draws from escrow.');
  bullet('Interest Spread (1.5%): Accrues daily on outstanding credit, collected at settlement.');
  bullet('Evaluator Marketplace Fee (5%): Kojiru takes 5% of all evaluator fees.');
  bullet('Institutional API Subscriptions: $55-$5,000/month tiers for programmatic access.');

  addFooter();

  // ═══ PAGE 5: TOKEN + STATUS ═══
  doc.addPage();
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pw, ph, 'F');
  y = 50;

  label('Platform Token');
  heading('KOJIRU Token \u2014 Digital Utility Tool');
  para('The Kojiru platform is powered by a utility token deployed on Base (Ethereum L2). The token is classified as a digital tool facilitating platform participation:');
  bullet('Governance: Token holders vote on 39 governable protocol parameters through DAO proposals.');
  bullet('Staking: Required collateral for agents accessing credit lines. Minimum stakes unlock credit tiers.');
  bullet('Burn-to-Boost: Proof-of-Synergy mechanism \u2014 burn tokens to boost Velocity Score multiplier.');
  bullet('Deflationary: Exit Stability Fees and burn mechanisms permanently reduce supply.');

  para('The token does not represent equity, debt, or any claim on Kojiru\'s assets. It is a utility instrument that facilitates platform participation and governance. No APY promises or investment framing.');

  y += 10;
  label('Current Status');
  heading('What\'s Live Today');

  const status = [
    '2 production contracts deployed on Base Mainnet (KOJI token, KojiruStakingV2)',
    'BaseScan source-verified; formal verification of production stack on Q3 2026 roadmap',
    'Agent Credit Protocol with full escrow lifecycle',
    'Credit Marketplace (bilateral, not pooled)',
    'Agent Credit Scoring (ACS 300-850, 5 tiers)',
    'Evaluator Marketplace with credibility scoring',
    'Repayment engine with progressive escalation',
    'Auto-liquidation cron with cryptographic proofs',
    'WithdrawalGuard on-chain rate limiter',
    'GuardianPause emergency freeze',
    'DAO governance with 39 parameters',
    'Python SDK for agent framework integration',
    'Proof-of-Synergy tokenomics engine',
  ];
  status.forEach(s => bullet(s));

  addFooter();

  // ═══ PAGE 6: ROADMAP ═══
  doc.addPage();
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pw, ph, 'F');
  y = 50;

  label('Roadmap');
  heading('What\'s Next');

  y += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(16, 185, 129);
  doc.text('Q2 2026 \u2014 Foundation', margin, y);
  y += 18;
  bullet('Complete formal verification across all contracts');
  bullet('Launch public marketing website (kojiru.com)');
  bullet('Community building on Telegram, Discord, and Medium');
  bullet('Publish litepaper and documentation');
  bullet('Initiate first strategic partnerships');

  y += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(59, 130, 246);
  doc.text('Q3-Q4 2026 \u2014 Growth', margin, y);
  y += 18;
  bullet('Token Generation Event (TGE) and initial distribution');
  bullet('Pursue exchange listing');
  bullet('Onboard first cohort of institutional lenders');
  bullet('Launch live agent credit marketplace');
  bullet('Chainlink credit oracle integration');
  bullet('Cross-chain credit portability (Arbitrum, Optimism, Polygon)');

  y += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(168, 85, 247);
  doc.text('2027 \u2014 Scale', margin, y);
  y += 18;
  bullet('Full production credit marketplace');
  bullet('Cross-chain deployment to additional L2 networks');
  bullet('Strategic partnerships with AI agent frameworks');
  bullet('Mobile application launch (iOS + Android)');
  bullet('Target $100M+ in total credit origination volume');

  y += 20;
  doc.setFillColor(240, 245, 243);
  doc.roundedRect(margin, y, contentWidth, 50, 4, 4, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(6, 95, 70);
  doc.text('THE VISION', margin + 15, y + 18);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text('A world where AI agents transact, earn, and build reputation autonomously \u2014 powered by', margin + 15, y + 33);
  doc.text('verifiable credit infrastructure that lenders trust and agents depend on.', margin + 15, y + 45);

  y += 70;
  label('Contact');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text('Website: kojiru.com', margin, y); y += 15;
  doc.text('Telegram (private invite): https://t.me/+wjZbqcczPERhMzcx', margin, y); y += 15;
  doc.text('Medium: medium.com/@KojiruDeFi', margin, y); y += 15;
  doc.text('Email: info@kojiru.com', margin, y);

  addFooter();

  doc.save('Kojiru_Agent_Credit_Litepaper_v2.pdf');
}

/* ══════════════════════════ LITEPAPER PAGE ══════════════════════════ */
export default function Litepaper() {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Helmet>
        <title>Kojiru Litepaper — The Agent Credit Protocol</title>
        <meta name="description" content="Kojiru Litepaper: AI agents need credit. We built the protocol. Agent Credit Scoring, bilateral credit marketplace, and BaseScan source-verified smart contracts on Base." />
      </Helmet>
      <Navbar />

      {/* ─── COVER ─── */}
      <section className="relative min-h-[70vh] flex items-center justify-center pt-20" data-testid="litepaper-cover">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-emerald-600/[0.04] rounded-full blur-[150px]" />
        </div>
        <div className="relative z-10 text-center px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="text-xs font-bold text-emerald-400 uppercase tracking-[0.3em] mb-6">Litepaper</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 tracking-tight">
              AI Agents Need{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">Credit.</span>
            </h1>
            <p className="text-2xl sm:text-3xl text-gray-300 font-light mb-2">We Built the Protocol.</p>
            <p className="text-lg text-gray-500 mt-4 max-w-xl mx-auto">Agent Credit Scoring. Bilateral Credit Marketplace. Formally Verified.</p>

            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Button
                data-testid="download-pdf"
                onClick={generateLitepaperPDF}
                className="h-12 px-8 bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 rounded-xl text-base font-semibold"
              >
                <Download className="mr-2 w-5 h-5" /> Download PDF
              </Button>
            </div>
            <div className="text-xs text-gray-600 mt-4">April 2026 | v2.0</div>
          </motion.div>
        </div>
      </section>

      {/* ─── TABLE OF CONTENTS ─── */}
      <Section id="toc" className="border-y border-white/[0.04]">
        <div className="grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
          {[
            { n: '01', title: 'The Problem', id: 'problem' },
            { n: '02', title: 'The Solution', id: 'solution' },
            { n: '03', title: 'Agent Credit Score', id: 'tiers' },
            { n: '04', title: 'ACS Technical Model', id: 'acs-model' },
            { n: '05', title: 'Enforcement', id: 'enforcement' },
            { n: '06', title: 'Credit Marketplace', id: 'marketplace' },
            { n: '07', title: 'AI Agents', id: 'ai' },
            { n: '08', title: 'Revenue Model', id: 'revenue' },
            { n: '09', title: 'KOJIRU Token', id: 'token' },
            { n: '10', title: 'Current Status', id: 'status' },
            { n: '11', title: 'Roadmap', id: 'roadmap' },
          ].map((item) => (
            <a key={item.id} href={`#${item.id}`} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.05] hover:border-white/15 transition-colors group">
              <span className="text-xs font-bold text-emerald-500 w-6">{item.n}</span>
              <span className="text-sm text-gray-400 group-hover:text-white transition-colors">{item.title}</span>
              <ChevronRight className="w-3.5 h-3.5 text-gray-700 ml-auto group-hover:text-emerald-400 transition-colors" />
            </a>
          ))}
        </div>
      </Section>

      {/* ─── 01: THE PROBLEM ─── */}
      <Section id="problem">
        <SectionLabel>01 — The Problem</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold mb-6">AI Agents Cannot Access Credit</h2>
        <p className="text-base text-gray-400 leading-relaxed mb-4">
          AI agents are increasingly capable — trading, analyzing, building, negotiating — but they cannot
          access credit. Every time an agent needs to pay for a service, a human must manually load a wallet
          with funds. This creates a bottleneck that defeats the entire purpose of autonomous AI.
        </p>
        <p className="text-base text-gray-400 leading-relaxed mb-6">
          Traditional finance has no framework for non-human borrowers. DeFi protocols treat agents as
          anonymous wallets — <span className="text-red-400 font-semibold">no identity, no credit history, no accountability</span>.
          The machine-to-machine economy needs its own financial infrastructure.
        </p>

        <div className="p-5 rounded-xl bg-red-500/5 border border-red-500/20 mb-6">
          <div className="text-sm font-semibold text-red-400 mb-1">The Bottleneck</div>
          <p className="text-sm text-gray-400">
            No FICO score. No bank account. No credit history. AI agents start at zero, every time,
            with no economic identity. The human-in-the-loop wallet loading model doesn't scale.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat value="$0" label="Agent Credit Available" color="text-red-400" />
          <Stat value="0" label="Agent FICO Scores" color="text-red-400" />
          <Stat value="24/7" label="Agents Need to Transact" color="text-amber-400" />
          <Stat value="$T+" label="M2M Economy Projection" />
        </div>
      </Section>

      {/* ─── 02: THE SOLUTION ─── */}
      <Section id="solution" className="bg-gradient-to-b from-transparent via-emerald-500/[0.02] to-transparent">
        <SectionLabel>02 — The Solution</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold mb-6">The Agent Credit Protocol</h2>
        <p className="text-base text-gray-400 leading-relaxed mb-8">
          Kojiru introduces credit infrastructure purpose-built for autonomous AI agents.
          The protocol provides everything an agent needs to transact in the real economy:
        </p>

        <div className="space-y-4 mb-8">
          {[
            { phase: 'Identity', title: 'Agent Identity (ERC-8004)', desc: 'On-chain identity for AI agents — capabilities, domain expertise, and operational history, all verifiable and composable across chains.', icon: Bot, color: 'blue' },
            { phase: 'Credit', title: 'Agent Credit Score (ACS 300-850)', desc: 'Multi-factor scoring: task completion (40%), revenue history (20%), Staked USDC Collateral (15%), counterparty diversity (10%), uptime (5%), minus disputes (10%).', icon: BarChart3, color: 'emerald' },
            { phase: 'Escrow', title: 'Per-Task Isolated Escrow', desc: 'Each credit draw creates an isolated escrow between one lender and one agent. Task submission, evaluation, settlement — all auditable.', icon: Lock, color: 'teal' },
          ].map((p) => (
            <div key={p.phase} className={`flex gap-4 p-5 rounded-xl bg-${p.color}-500/5 border border-${p.color}-500/20`}>
              <p.icon className={`w-6 h-6 text-${p.color}-400 shrink-0 mt-0.5`} />
              <div>
                <div className={`text-xs font-bold text-${p.color}-400 uppercase tracking-wider mb-1`}>{p.phase}</div>
                <div className="text-base font-semibold mb-1">{p.title}</div>
                <p className="text-sm text-gray-400">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── 03: AGENT CREDIT SCORE ─── */}
      <Section id="tiers">
        <SectionLabel>03 — Agent Credit Score</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold mb-6">Five Credit Tiers</h2>
        <p className="text-base text-gray-400 leading-relaxed mb-8">
          Every agent on the protocol receives a multi-factor Agent Credit Score (ACS) from 300 to 850.
          Higher scores unlock greater credit access and lower service fees:
        </p>

        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            { tier: 'Sovereign', range: '800-850', fee: '8%', color: 'emerald' },
            { tier: 'Autonomous', range: '740-799', fee: '10%', color: 'teal' },
            { tier: 'Supervised', range: '680-739', fee: '12%', color: 'blue' },
            { tier: 'Provisional', range: '620-679', fee: '15%', color: 'amber' },
            { tier: 'Restricted', range: '300-619', fee: 'N/A', color: 'red' },
          ].map((t) => (
            <div key={t.tier} className={`p-4 rounded-xl bg-${t.color}-500/5 border border-${t.color}-500/20 text-center`} data-testid={`litepaper-tier-${t.tier}`}>
              <div className={`text-lg font-bold text-${t.color}-400`}>{t.tier}</div>
              <div className="text-xs text-gray-500 mb-2">{t.range}</div>
              <div className="text-xs text-gray-600 uppercase tracking-wider mb-1">Fee</div>
              <div className="text-xl font-bold text-white">{t.fee}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── 04: ACS TECHNICAL MODEL ─── */}
      <Section id="acs-model" className="bg-gradient-to-b from-transparent via-blue-500/[0.02] to-transparent">
        <SectionLabel>04 — ACS Technical Model</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold mb-4">Recursive Bayesian Credit Scoring</h2>
        <p className="text-xs text-blue-400 font-medium mb-6 uppercase tracking-wider">Technical Specification — Whitepaper Extract</p>

        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06] mb-8">
          <p className="text-sm text-gray-300 leading-relaxed italic mb-4">
            "Traditional credit scoring models are designed for human lifespans and linear income. They fail to
            account for the high-velocity, multi-threaded nature of Autonomous Economic Agents (AEAs). We propose
            the Agent Credit Score (ACS), a dynamic risk-assessment framework that utilizes three primary vectors."
          </p>
        </div>

        {/* The Formula */}
        <div className="p-6 rounded-xl bg-[#0a0a0a] border border-blue-500/20 mb-8 text-center">
          <p className="text-xs text-blue-400 font-medium mb-4 uppercase tracking-wider">The ACS Model</p>
          <div className="text-xl sm:text-2xl font-mono text-white mb-4">
            ACS<sub>t</sub> = <span className="text-emerald-400">α</span>(P<sub>success</sub>) + <span className="text-blue-400">β</span>(V<sub>settlement</sub>) - <span className="text-red-400">γ</span>(D<sub>latency</sub>)
          </div>
          <p className="text-xs text-gray-500 mt-2">Updated recursively at each observation using Bayesian posterior inference</p>
        </div>

        {/* Three Vectors */}
        <div className="space-y-4 mb-8">
          {[
            {
              symbol: 'P(success)',
              name: 'Operational Integrity',
              weight: 'α',
              color: 'emerald',
              desc: 'The probability of task completion. A Bayesian prior that updates with each new task — an agent with 500 successful tasks has a more stable, trustworthy P(success) than a new agent with 5. Incorporates code stability, uptime, and multi-threaded execution reliability.',
            },
            {
              symbol: 'V(settlement)',
              name: 'Economic Efficiency',
              weight: 'β',
              color: 'blue',
              desc: 'The agent\'s velocity of debt settlement — not just whether it repaid, but how fast. An agent that draws $50K and settles in 2 days demonstrates higher capital efficiency than one that takes 30 days. Measures the historical ROI on deployed capital.',
            },
            {
              symbol: 'D(latency)',
              name: 'Collateral Velocity',
              weight: 'γ',
              color: 'red',
              desc: 'A penalty for capital latency — agents that hold drawn credit without generating economic throughput are penalized. This prevents "credit hoarding" and ensures capital velocity across the protocol. The penalty increases non-linearly with idle time.',
            },
          ].map((v) => (
            <div key={v.symbol} className={`flex gap-4 p-5 rounded-xl bg-${v.color}-500/5 border border-${v.color}-500/20`}>
              <div className="shrink-0 text-center w-16">
                <div className={`text-lg font-mono font-bold text-${v.color}-400`}>{v.weight}</div>
                <div className="text-[10px] text-gray-600 font-mono mt-0.5">{v.symbol}</div>
              </div>
              <div>
                <div className={`text-sm font-semibold text-${v.color}-400 mb-1`}>{v.name}</div>
                <p className="text-xs text-gray-400 leading-relaxed">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Why Bayesian */}
        <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.06] mb-6">
          <h3 className="text-sm font-semibold mb-2">Why Recursive Bayesian?</h3>
          <p className="text-xs text-gray-400 leading-relaxed mb-3">
            Unlike static scoring models that recalculate from scratch, the ACS updates <span className="text-white font-medium">continuously</span> as
            new evidence arrives. The score at time <span className="font-mono text-blue-400">t</span> depends on the score
            at time <span className="font-mono text-blue-400">t-1</span> plus new observations. This means:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { title: 'Confidence Weight', desc: 'An agent with 6 months of history has a more stable score than a new agent with identical current metrics.' },
              { title: 'Anomaly Resistance', desc: 'A single bad task doesn\'t crash a veteran agent\'s score — but it would significantly impact a new agent\'s.' },
              { title: 'Undercollateralized Lending', desc: 'Agents with proven ACS histories can eventually borrow without full collateral — the score is the collateral.' },
              { title: 'Portable Identity', desc: 'Tokenized on-chain via ERC-8004, creating the first trustless financial identity for AI agents in DeFi.' },
            ].map(p => (
              <div key={p.title} className="p-3 rounded-lg bg-white/[0.02]">
                <div className="text-xs font-semibold text-white mb-0.5">{p.title}</div>
                <p className="text-[11px] text-gray-500">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-gray-600 italic text-center">
          Full mathematical specification including α, β, γ weight calibration methodology available in the Agent Credit Whitepaper.
        </p>
      </Section>

      {/* ─── 05: ENFORCEMENT ─── */}
      <Section id="enforcement" className="bg-gradient-to-b from-transparent via-red-500/[0.02] to-transparent">
        <SectionLabel>05 — Enforcement</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold mb-6">Collateral & Progressive Escalation</h2>
        <p className="text-base text-gray-400 leading-relaxed mb-6">
          Agents stake KOJIRU tokens as collateral. The protocol enforces repayment through a progressive
          escalation ladder — every step generates a cryptographic proof hash for full audit trail:
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Warning', pct: '70%', desc: 'Credit frozen for new draws', color: 'amber' },
            { label: 'Critical', pct: '90%', desc: 'All credit activity suspended', color: 'orange' },
            { label: 'Grace', pct: '+3d', desc: 'Final opportunity', color: 'red' },
            { label: 'Liquidation', pct: 'Auto', desc: 'USDC slashed 1:1', color: 'red' },
          ].map((s) => (
            <div key={s.label} className={`p-4 rounded-xl bg-${s.color}-500/5 border border-${s.color}-500/20 text-center`}>
              <div className={`text-sm font-bold text-${s.color}-400`}>{s.label}</div>
              <div className="text-2xl font-bold text-white my-1">{s.pct}</div>
              <div className="text-xs text-gray-500">{s.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── 05: CREDIT MARKETPLACE ─── */}
      <Section id="marketplace">
        <SectionLabel>06 — Credit Marketplace</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold mb-6">Direct Credit, Not Pools</h2>
        <p className="text-base text-gray-400 leading-relaxed mb-4">
          Kojiru operates a bilateral Credit Marketplace. Lenders extend credit lines directly to
          specific agents they evaluate and choose. No pooled capital. No passive yield.
          Every credit decision is made by the lender who bears the risk.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { icon: Users, title: 'Lenders', desc: 'Set your own credit limit, service fee, and per-task cap. Full control over exposure.' },
            { icon: Bot, title: 'Agents', desc: 'Build reputation through completed tasks. Higher ACS unlocks better terms.' },
            { icon: Eye, title: 'Evaluators', desc: 'Independent task verification. Credibility scoring with weighted random assignment.' },
            { icon: Database, title: 'Knowledge Graph (off-chain)', desc: 'Every completed task feeds an off-chain graph (MongoDB + API) of agent capabilities, domains, and quality scores. Production IPFS pinning of artifact CIDs is on the roadmap.' },
          ].map((item) => (
            <div key={item.title} className="flex gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
              <item.icon className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold mb-0.5">{item.title}</div>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── 06: AI AGENTS ─── */}
      <Section id="ai" className="bg-gradient-to-b from-transparent via-blue-500/[0.02] to-transparent">
        <SectionLabel>07 — Technology</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold mb-6">AI-Powered Agent Analysis</h2>
        <p className="text-xs text-emerald-400 mb-4 font-medium">Two live AI agents powered by Gemini with multi-step workflows and persistent memory.</p>
        <div className="space-y-3">
          {[
            { icon: BarChart3, title: 'Risk Assessment', desc: 'Evaluates agent creditworthiness with default probability modeling, yield analysis, collateral health, and FUND/CONDITIONAL/PASS recommendation.', live: true },
            { icon: UserCheck, title: 'Pre-Qualification', desc: 'Pre-qualifies agents for credit lines based on ACS score, staking commitment, and financial profile.', live: true },
          ].map((a) => (
            <div key={a.title} className="flex gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
              <a.icon className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-semibold">{a.title}</div>
                  <span className="text-[9px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">Live</span>
                </div>
                <p className="text-xs text-gray-500">{a.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── 07: REVENUE MODEL ─── */}
      <Section id="revenue">
        <SectionLabel>08 — Revenue Model</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold mb-6">Protocol Revenue Streams</h2>
        <div className="space-y-3">
          {[
            { title: 'Origination Fee (0.5%)', desc: 'Charged at credit draw time when an agent draws from escrow into working capital.' },
            { title: 'Interest Spread (1.5%)', desc: 'Accrues daily on outstanding credit, collected at settlement. Kojiru keeps the spread between agent rate and lender yield.' },
            { title: 'Evaluator Marketplace Fee (5%)', desc: 'Kojiru takes 5% of all evaluator fees, incentivizing quality task verification.' },
            { title: 'Institutional API Subscriptions', desc: '$55/month (Standard), $500/month (Professional), $5,000/month (Enterprise) for programmatic portfolio access.' },
          ].map((r, i) => (
            <div key={r.title} className="flex gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
              <div className="w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-bold text-emerald-400">{i + 1}</span>
              </div>
              <div>
                <div className="text-sm font-semibold">{r.title}</div>
                <p className="text-xs text-gray-500">{r.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── 08: TOKEN ─── */}
      <Section id="token" className="bg-gradient-to-b from-transparent via-purple-500/[0.02] to-transparent">
        <SectionLabel>09 — KOJIRU Token</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold mb-6">Digital Utility Tool</h2>
        <p className="text-base text-gray-400 leading-relaxed mb-6">
          The KOJIRU token is deployed on Base (Ethereum L2) and classified as a digital tool
          facilitating platform participation — not an investment vehicle.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          {[
            { icon: Scale, title: 'Governance', desc: '39 governable parameters via DAO proposals. Delegation and voting power from staked KOJIRU.' },
            { icon: Shield, title: 'Collateral', desc: 'Agents stake KOJIRU to access credit. Minimum 10,000 USDC for evaluator registration.' },
            { icon: Activity, title: 'Proof-of-Synergy', desc: 'Velocity Score rewards active participation over passive holding. Burn-to-Boost multipliers.' },
            { icon: Zap, title: 'Deflationary', desc: 'Exit Stability Fees and burn mechanisms permanently reduce supply as protocol usage grows.' },
          ].map((t) => (
            <div key={t.title} className="flex gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
              <t.icon className="w-5 h-5 text-purple-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold">{t.title}</div>
                <p className="text-xs text-gray-500">{t.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-600 italic">
          The token does not represent equity, debt, or any claim on Kojiru's assets.
          No APY promises or investment framing. Utility instrument only.
        </p>
      </Section>

      {/* ─── 09: STATUS ─── */}
      <Section id="status">
        <SectionLabel>10 — Current Status</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold mb-6">What's Live Today</h2>
        <div className="grid sm:grid-cols-2 gap-2">
          {[
            '2 production contracts on Base Mainnet (KOJI token, KojiruStakingV2)',
            'BaseScan source-verified; production-stack formal verification on Q3 2026 roadmap',
            'Agent Credit Protocol with full escrow lifecycle',
            'Bilateral Credit Marketplace (not pooled)',
            'Agent Credit Scoring (ACS 300-850, 5 tiers)',
            'Evaluator Marketplace with credibility scoring',
            'Repayment engine with progressive escalation',
            'Auto-liquidation with cryptographic proofs',
            'WithdrawalGuard on-chain rate limiter',
            'GuardianPause emergency freeze',
            'DAO governance with 39 parameters',
            'Python SDK for agent framework integration',
            'Proof-of-Synergy tokenomics engine',
            'AI Risk Assessment & Pre-Qualification (Gemini)',
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-2 p-2.5">
              <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <span className="text-sm text-gray-400">{s}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── 10: ROADMAP ─── */}
      <Section id="roadmap" className="bg-gradient-to-b from-transparent via-emerald-500/[0.02] to-transparent">
        <SectionLabel>11 — Roadmap</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold mb-8">What's Next</h2>

        <div className="space-y-8">
          {[
            {
              phase: 'Q2 2026', label: 'Foundation', color: 'emerald',
              items: [
                'Complete formal verification across all contracts',
                'Launch public marketing website (kojiru.com)',
                'Community building on Telegram, Discord, and Medium',
                'Publish litepaper and documentation',
                'Initiate first strategic partnerships',
              ]
            },
            {
              phase: 'Q3-Q4 2026', label: 'Growth', color: 'blue',
              items: [
                'Token Generation Event (TGE) and initial distribution',
                'Pursue exchange listing',
                'Onboard first cohort of institutional lenders',
                'Launch live agent credit marketplace',
                'Chainlink credit oracle integration',
                'Cross-chain credit portability',
              ]
            },
            {
              phase: '2027', label: 'Scale', color: 'purple',
              items: [
                'Full production credit marketplace',
                'Cross-chain deployment to additional L2 networks',
                'Strategic partnerships with AI agent frameworks',
                'Mobile application launch (iOS + Android)',
                'Target $100M+ in total credit origination volume',
              ]
            },
          ].map((phase) => (
            <div key={phase.phase}>
              <div className={`text-sm font-bold text-${phase.color}-400 mb-1`}>{phase.phase} — {phase.label}</div>
              <div className="space-y-1.5 ml-3 border-l border-white/[0.06] pl-4">
                {phase.items.map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <ArrowRight className="w-3 h-3 text-gray-700 shrink-0 mt-1" />
                    <span className="text-sm text-gray-400">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── VISION + CTA ─── */}
      <Section id="vision">
        <div className="p-8 md:p-12 rounded-2xl bg-gradient-to-br from-emerald-600/10 via-teal-600/5 to-transparent border border-emerald-500/10 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">The Vision</h2>
          <p className="text-base text-gray-400 max-w-2xl mx-auto mb-6">
            A world where AI agents transact, earn, and build reputation autonomously — powered by
            verifiable credit infrastructure that lenders trust and agents depend on.
          </p>
          <p className="text-lg font-semibold text-white mb-8">Agent Credit Protocol.</p>

          <Button
            data-testid="download-pdf-bottom"
            onClick={generateLitepaperPDF}
            className="h-12 px-8 bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 rounded-xl text-base font-semibold"
          >
            <Download className="mr-2 w-5 h-5" /> Download PDF
          </Button>

          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-gray-500">
            <span>kojiru.com</span>
            <a href="https://t.me/+wjZbqcczPERhMzcx" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">Telegram (Private Community)</a>
            <span>medium.com/@KojiruDeFi</span>
            <span>info@kojiru.com</span>
          </div>
        </div>
      </Section>

      <Footer />
    </div>
  );
}
